import type { GenerationStatusTypes, ModelStatusTypes } from "@prisma/client";

type AnyStatus =
  | GenerationStatusTypes
  | ModelStatusTypes
  | "completed"
  | "processing"
  | "pending"
  | "training"
  | "failed"
  | "refused"
  | null
  | undefined;

/**
 * The user asked, a gate said no, nothing was attempted. Not a malfunction.
 *
 * Refusals are quotas, plan restrictions, lapsed subscriptions — the product working as
 * designed. They are stored separately from `failed` because conflating the two has
 * misdirected three investigations; see the enum comments in schema.prisma.
 */
export const isRefusal = (status: AnyStatus): boolean => status === "refused";

/**
 * Something we did not intend went wrong. This — and only this — belongs in a failure
 * rate, an error budget, or an alert.
 */
export const isFailure = (status: AnyStatus): boolean => status === "failed";

/**
 * Terminal, and produced nothing. The union of the two above, and almost always what UI
 * code wants: a refused row and a failed row look identical on screen — no audio, no
 * spinner, a reason to show — even though they mean opposite things about our reliability.
 *
 * Prefer this over `status === "failed"` at every render site. A bare equality check there
 * silently drops refused rows, leaving a generation that renders as neither finished nor
 * running, which reads to the user as the page being stuck.
 */
export const isFailureOrRefusal = (status: AnyStatus): boolean =>
  isFailure(status) || isRefusal(status);

/**
 * tRPC error codes that mean a gate said no, rather than something breaking.
 *
 * `INTERNAL_SERVER_ERROR` is deliberately absent, and that omission is the entire point of
 * this list. models.ts throws it when the Modal training call fails, which is a genuine
 * failure and the most important one to keep in the metric. Classifying every tRPC error as
 * a refusal would hide exactly those runs, inverting the bug this change exists to fix.
 * TIMEOUT and CLIENT_CLOSED_REQUEST are absent for the same reason: neither is a policy
 * decision.
 */
const REFUSAL_TRPC_CODES = new Set([
  "BAD_REQUEST",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "METHOD_NOT_SUPPORTED",
  "CONFLICT",
  "PRECONDITION_FAILED",
  "PAYLOAD_TOO_LARGE",
  "UNPROCESSABLE_CONTENT",
  "TOO_MANY_REQUESTS",
]);

/**
 * True when a thrown tRPC error represents a gate refusing the request. Anything without a
 * tRPC code, and any server-side error code, counts as a failure.
 */
export const isRefusalError = (err: unknown): boolean => {
  const code = (err as { data?: { code?: unknown } } | null | undefined)?.data
    ?.code;
  return typeof code === "string" && REFUSAL_TRPC_CODES.has(code);
};
